"use client";

import { c as _c } from "react/compiler-runtime";
import { type MotionValue, m, useMotionValue, useTransform } from "motion/react";
import { DemoWrapper } from "@/components/demos/demo-wrapper";
import { Slider } from "@/components/ui/slider";
const OKLCH_CHROMA = 0.16;
const HSL_SATURATION = 100;
const HUES = [30, 150, 250, 320];
const DEFAULT_LIGHTNESS = 50;
type Space = "oklch" | "hsl";
const toOklchLightness = (v: number) => Math.round(v) / 100;
const formatColor = (space: Space, hue: number, v: number) => space === "oklch" ? `oklch(${toOklchLightness(v).toFixed(2)} ${OKLCH_CHROMA} ${hue})` : `hsl(${hue} ${HSL_SATURATION}% ${Math.round(v)}%)`;
interface SampleProps {
  colorSpace: Space;
  hue: number;
  lightness: MotionValue<number>;
}
const ColoredPill = t0 => {
  const $ = _c(18);
  const {
    hue,
    lightness,
    colorSpace
  } = t0;
  let t1;
  if ($[0] !== colorSpace || $[1] !== hue || $[2] !== lightness) {
    t1 = () => formatColor(colorSpace, hue, lightness.get());
    $[0] = colorSpace;
    $[1] = hue;
    $[2] = lightness;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const bgColor = useTransform(t1);
  let t2;
  if ($[4] !== colorSpace || $[5] !== hue || $[6] !== lightness) {
    t2 = () => {
      const v = lightness.get();
      if (colorSpace === "oklch") {
        const l = toOklchLightness(v);
        const tl = l > 0.4 ? Math.max(0, l - 0.4) : Math.min(1, l + 0.4);
        return `oklch(${tl.toFixed(2)} ${OKLCH_CHROMA} ${hue})`;
      }
      const l_0 = Math.round(v);
      const tl_0 = l_0 > 40 ? Math.max(0, l_0 - 30) : Math.min(100, l_0 + 30);
      return `hsl(${hue} ${HSL_SATURATION}% ${tl_0}%)`;
    };
    $[4] = colorSpace;
    $[5] = hue;
    $[6] = lightness;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const textColor = useTransform(t2);
  let t3;
  if ($[8] !== bgColor) {
    t3 = {
      backgroundColor: bgColor
    };
    $[8] = bgColor;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== textColor) {
    t4 = {
      color: textColor
    };
    $[10] = textColor;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== colorSpace || $[13] !== t4) {
    t5 = <m.span className="font-berkeley-mono font-medium" style={t4}>{colorSpace}</m.span>;
    $[12] = colorSpace;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t5) {
    t6 = <div className="flex w-full max-w-28 select-none items-center justify-center rounded-full bg-surface p-1 shadow-raised"><m.div className="flex w-full select-none items-center justify-center rounded-full py-1.5 outline-subtle" style={t3}>{t5}</m.div></div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const ValueLabel = t0 => {
  const $ = _c(6);
  const {
    hue,
    lightness,
    colorSpace
  } = t0;
  let t1;
  if ($[0] !== colorSpace || $[1] !== hue || $[2] !== lightness) {
    t1 = () => formatColor(colorSpace, hue, lightness.get());
    $[0] = colorSpace;
    $[1] = hue;
    $[2] = lightness;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const text = useTransform(t1);
  let t2;
  if ($[4] !== text) {
    t2 = <m.span className="font-berkeley-mono font-medium text-fg">{text}</m.span>;
    $[4] = text;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export function BrightnessExample() {
  const $ = _c(22);
  const lightness = useMotionValue(DEFAULT_LIGHTNESS);
  let t0;
  if ($[0] !== lightness) {
    t0 = () => `${Math.round(lightness.get())}%`;
    $[0] = lightness;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const percent = useTransform(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["oklch", "hsl"];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== lightness) {
    t2 = <div className="flex w-full flex-col gap-3 px-4 py-8">{(t1 as const).map(space => <div className="grid w-full grid-cols-2 justify-items-center gap-3 sm:grid-cols-4" key={space}>{HUES.map(hue => <ColoredPill colorSpace={space} hue={hue} key={hue} lightness={lightness} />)}</div>)}</div>;
    $[3] = lightness;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["oklch", "hsl"];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== lightness) {
    t4 = <div className="grid w-full grid-cols-1 border-gray-400 border-t bg-gray-100 sm:grid-cols-2">{(t3 as const).map(space_0 => <div className="flex w-full select-none flex-col items-start justify-start gap-2 border-gray-400 px-4 py-3 first:border-b sm:first:border-r sm:first:border-b-0" key={space_0}>{HUES.map(hue_0 => <ValueLabel colorSpace={space_0} hue={hue_0} key={hue_0} lightness={lightness} />)}</div>)}</div>;
    $[6] = lightness;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="flex w-full flex-col items-center justify-center">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="select-none text-fg">Lightness</span>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== percent) {
    t7 = <div className="flex w-full items-center justify-between text-sm">{t6}<m.span className="font-berkeley-mono text-fg">{percent}</m.span></div>;
    $[12] = percent;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== lightness) {
    t8 = <Slider aria-label="Lightness" defaultValue={DEFAULT_LIGHTNESS} max={100} min={0} onValueChange={value => lightness.set(value)} step={1} />;
    $[14] = lightness;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <div className="flex min-h-15.25 w-full items-center justify-center border-surface-border border-t bg-gray-100 px-4 py-3"><div className="flex w-full flex-col gap-3">{t7}{t8}</div></div>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <DemoWrapper>{t5}{t9}</DemoWrapper>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}